import { SearchOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Col, Input, Pagination, Popconfirm, Row, Space, Table, Tooltip, message } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ReactComponent as ArchiveRestore } from "../../Images/ArchiveRestore.svg";
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import { fetchArchivedUsersList, resetUserList, unArchiveUser } from './../../slices/user';

export default function ArchiveUserList() {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const debounceTimeout = useRef(null);

    const rowSelection = {
        selectedRowKeys,
        onChange: selectedRowKeys => {
            setSelectedRowKeys(selectedRowKeys);
        },
    };

    const dispatch = useDispatch();
    const { data = [], totalRecord, status } = useSelector((state) => state.user);
    const [search, setSearch] = useState('');
    const [orderBy, setOrderBy] = useState('');
    const [page, setPage] = useState(1);
    const [pagesize, setPageSize] = useState(10);

    useEffect(() => {
        dispatch(resetUserList());
        dispatch(fetchArchivedUsersList({ page, pagesize, ...(search && { search: debouncedSearch }), ...(orderBy && { ordering: orderBy }) }));
    }, [page, pagesize, debouncedSearch, orderBy]);

    useEffect(() => {
        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, []);

    const handleSearch = (e) => {
        setSearch(e.target.value); // Update immediately

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            setDebouncedSearch(e.target.value); // Set debounced value after delay
            setPage(1); // Reset to page 1 on new search
        }, 0); // 500ms debounce delay
    };

    const resetFilter = () => {
        setDebouncedSearch('');
        setSearch('');
        setOrderBy('');
        setPage(1);
    };
    const handlePageChange = (newPage) => {
        setPage(newPage);
    };
    const GetSortingEnumValue = (sortField, sortOrder) => {
        let sortingEnumKey = "id";

        if (sortField === "username" && sortOrder === "ascend") {
            sortingEnumKey = "username";
        }
        if (sortField === "username" && sortOrder === "descend") {
            sortingEnumKey = "-username";
        }
        if (sortField === "first_name" && sortOrder === "ascend") {
            sortingEnumKey = "first_name";
        }
        if (sortField === "first_name" && sortOrder === "descend") {
            sortingEnumKey = "-first_name";
        }
        if (sortField === "last_name" && sortOrder === "ascend") {
            sortingEnumKey = "last_name";
        }
        if (sortField === "last_name" && sortOrder === "descend") {
            sortingEnumKey = "-last_name";
        }
        if (sortField === "email" && sortOrder === "ascend") {
            sortingEnumKey = "email";
        }
        if (sortField === "email" && sortOrder === "descend") {
            sortingEnumKey = "-email";
        }
        if (sortField === "phone" && sortOrder === "ascend") {
            sortingEnumKey = "phone";
        }
        if (sortField === "phone" && sortOrder === "descend") {
            sortingEnumKey = "-phone";
        }
        return sortingEnumKey;
    };
    const handleTableChange = async (pagination, sorter) => {
        const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
        setPageSize(pagination.pageSize);
        setOrderBy(orderBy);

    };
    const handleRestore = (id) => {
        if (!id) {
            message.error('Missing ID for deletion!');
            return;
        }

        dispatch(unArchiveUser(id))
            .unwrap()
            .then(() => {
                message.success('User Unarchived Successfully!');
                dispatch(fetchArchivedUsersList({ page, pagesize, ...(search && { search: debouncedSearch }), ...(orderBy && { ordering: orderBy }) }));
            })
            .catch((error) => {
                if (typeof error === 'object' && error !== null) {
                    const messages = [];
                    for (const key in error) {
                        if (Array.isArray(error[key])) {
                            messages.push(`${key}: ${error[key].join(', ')}`);
                        }
                    }

                    if (messages.length > 0) {
                        message.error(messages.join('; '));
                    } else {
                        message.error('An unknown error occurred.');
                    }
                } else if (typeof error === 'string') {
                    message.error(error); // Handle string error
                } else if (error instanceof Error) {
                    message.error(error.message); // Handle Error object
                } else {
                    message.error('An unknown error occurred.');
                }
            });
    };

    const [checkedList, setCheckedList] = useState([
        'username',
        'first_name',
        'last_name',
        'email',
        'phone',
        'status',
        'id',
        'updated_at',
        'updated_by',
        'action',
    ]);

    const columns = [
        {
            title: 'Sr. No.',
            dataIndex: 'id',
            key: 'id',
            width: 80,
            render: (text, record, index) => (page - 1) * pagesize + index + 1,
            sorter: false,
        },
        {
            title: 'User Name',
            dataIndex: 'username',
            key: 'username',
            sorter: true,
        },
        {
            title: 'First Name',
            dataIndex: 'first_name',
            key: 'first_name',
            sorter: true,
        },
        {
            title: 'Last Name',
            dataIndex: 'last_name',
            key: 'last_name',
            sorter: true,
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            sorter: true,
        },
        {
            title: "Contact",
            dataIndex: "phone",
            key: "phone",
            width: 180,
            render: (phone) => {
                if (phone) {
                    const cleanedPhone = String(phone).replace(/\D/g, "");

                    if (cleanedPhone.startsWith("91")) {
                        return `+91 ${cleanedPhone.slice(2, 7)} ${cleanedPhone.slice(7)}`;
                    } else if (cleanedPhone.length > 5) {
                        const countryCode = cleanedPhone.slice(0, cleanedPhone.length - 10);
                        const localNumber = cleanedPhone.slice(-10);
                        return `+${countryCode} ${localNumber.slice(
                            0,
                            5
                        )} ${localNumber.slice(5)}`;
                    } else {
                        return `+${cleanedPhone}`;
                    }
                }
                return null;
            },

            sorter: true,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: true,
        },
        {
            title: 'Archive At',
            dataIndex: 'updated_at',
            key: 'updated_at',
            sorter: true,
            render: (record) => {
                return (
                    <span>{record ? dayjs(record).format('DD-MM-YYYY HH:mm') : 'N/A'}</span>
                )
            }
        },
        {
            title: 'Archive By',
            dataIndex: 'updated_by',
            key: 'updated_by',
            sorter: true,
            render: (record) => {
                return (
                    <span>{record ? `${record.first_name} ${record.last_name}` : 'N/A'}</span>
                )
            }
        },
        {
            title: 'Action',
            dataIndex: 'id', // Use `id` here for proper alignment and access
            key: 'action',
            render: (text, record) => (
                <Space>
                    <Tooltip placement="top" title="Restore">
                        <Popconfirm
                            title="Are you sure you want to Unarchive this record?"
                            onConfirm={() => handleRestore(record.id)} // Trigger delete on confirmation
                            onCancel={() => message.info('Action cancelled')} // Optional cancel action
                            okText="Yes"
                            cancelText="No"
                        >
                            <ArchiveRestore className="ArchiverestorImg" />
                        </Popconfirm>
                    </Tooltip>
                </Space>
            ),
        },

    ];
    const filteredColumns = columns.filter(column =>
        checkedList.includes(column.key),
    );

 const handlePageSizeChange = (current, size) => {
    setPage(1); // Reset to first page if needed
    setPageSize(size); // You must have a setter or state update
  };
    return (
        <div>
            <div className='sticyaction'>
                <Row className="pagenamerow" justify="space-between" align="middle">
                    <Col>
                        <h2 style={{ margin: 0 }}>Archived Users</h2>
                        <div className="bredcrumbwrp">
                            <Link to="/manage-user" className="back">
                                <Back /> BACK
                            </Link>
                            <Breadcrumb
                                items={[
                                    { title: <Link to="/dashboard">Home</Link> },
                                    { title: <Link to="/manage-user">Users</Link> },
                                    { title: 'Archived Users' }
                                ]}
                            />
                        </div>
                    </Col>
                    <Col>
                        <Space size="small" >
                            <Input
                                placeholder="Search"
                  className='searchbox'
                                prefix={<SearchOutlined />}
                                value={search}
                                onChange={handleSearch}
                                style={{ width: 200 }}
                            />
                            <Tooltip placement="top" title="Reset Filter">
                                <Button type="primary" disabled={!search && !orderBy} className='iconlink' onClick={resetFilter}>
                                    <FilterIcon />
                                </Button>
                            </Tooltip>
                        </Space>
                    </Col>
                </Row>
            </div>
              <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, overflow: 'auto' }}>
            <Table
                dataSource={data}
                columns={filteredColumns}
                ///rowSelection={rowSelection}
                loading={status === 'loading'}
                // pagination={{
                //     current: page, // Update the current page based on state
                //     pageSize: pagesize,
                //     total: totalRecord,
                //     showSizeChanger: true,
                //     showTotal: (total) => `Total ${total} items`,
                //     onChange: handlePageChange, // Update page when changed
                // }}
                pagination={false}
                onChange={handleTableChange}
                className="mt-5"
            />
             </div>
                    <div
                      style={{
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 1000,
                        background: '#fff',
                        padding: 10,
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Pagination
                        current={page}
                        pageSize={pagesize}
                        total={totalRecord}
                        showSizeChanger
                        onShowSizeChange={handlePageSizeChange}
                        showTotal={total => `Total ${total} items`}
                        onChange={handlePageChange}
                        locale={{ items_per_page: 'Items / Page' }}
                      />
                    </div>
                    </div>
        </div>
    );
}
